// The /admin/users filters live in the URL (constitution §5.6), so they survive a reload, the Back
// button and a shared link. Every value is checked here; anything else in the URL is dropped.
import { ROLES, type Role } from '@cis/shared';
import type { UserListParams } from '@cis/api-client';

/** At most 50 people (spec P1), so one page usually holds everyone. */
export const USERS_PAGE_SIZE = 50;
export const STATUSES = ['active', 'inactive'] as const;
export type UserStatus = (typeof STATUSES)[number];

const MAX_PAGE = 1000;
// Page 1 is the default and is kept out of the URL.
const FIRST_LISTED_PAGE = 2;
export const MAX_QUERY = 100;

export interface UsersSearch {
  readonly q?: string | undefined;
  readonly role?: Role | undefined;
  readonly status?: UserStatus | undefined;
  readonly page?: number | undefined;
}

const oneOf = <T extends string>(list: readonly T[], v: unknown): T | undefined =>
  list.find((item) => item === v);

// The router parses "123" in the URL as a number; the search box takes it back as text.
function textOf(v: unknown): string | undefined {
  if (typeof v !== 'string' && typeof v !== 'number') return undefined;
  const text = String(v).trim();
  return text === '' || text.length > MAX_QUERY ? undefined : text;
}

function pageOf(v: unknown): number | undefined {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
  return typeof n === 'number' && Number.isInteger(n) && n >= FIRST_LISTED_PAGE && n <= MAX_PAGE
    ? n
    : undefined;
}

/** Every key is returned, so an unchecked raw value cannot survive the router's merge. */
export function validateUsersSearch(search: Readonly<Record<string, unknown>>): UsersSearch {
  return {
    q: textOf(search['q']),
    role: oneOf(ROLES, search['role']),
    status: oneOf(STATUSES, search['status']),
    page: pageOf(search['page']),
  };
}

/** The API query for these filters. */
export function listParamsOf(s: UsersSearch): UserListParams {
  return {
    page: s.page ?? 1,
    pageSize: USERS_PAGE_SIZE,
    q: s.q,
    role: s.role,
    active: s.status === undefined ? undefined : s.status === 'active',
  };
}

export const hasFilters = (s: UsersSearch): boolean =>
  s.q !== undefined || s.role !== undefined || s.status !== undefined;
